import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CaretDown } from "@phosphor-icons/react/CaretDown";
import { CaretRight } from "@phosphor-icons/react/CaretRight";
import { Question } from "@phosphor-icons/react/Question";
import * as React from "react";
import { sectionSpacing } from "./section-spacing";
import { headingFontFamily } from "../heading-font";
import { FaqSchema } from "~landing/seo/faq-schema";

export interface FaqItem {
	id: string;
	question: string;
	answer: string;
}

export interface FaqsContent {
	faqContent: {
		eyebrow: string;
		heading: string;
		subHeading: React.ReactNode;
	};
	faqs: FaqItem[];
}

export function Faqs({ content }: { content: FaqsContent }) {
	const { faqContent, faqs } = content;

	return (
		<Box
			sx={{
				bgcolor: "var(--mui-palette-background-level1)",
				py: sectionSpacing,
			}}>
			<FaqSchema faqs={faqs} />
			<Container maxWidth="md">
				<Stack spacing={8}>
					<Stack
						maxWidth="700px"
						sx={{ mx: "auto" }}>
						<Stack spacing={2}>
							<Box sx={{ display: "flex", justifyContent: "center" }}>
								<Chip
									color="secondary"
									icon={<Question />}
									label={faqContent.eyebrow}
									size="small"
									variant="soft"
								/>
							</Box>
							<Typography
								sx={{ textAlign: "center", fontFamily: headingFontFamily }}
								variant="h5">
								{faqContent.heading}
							</Typography>
							{faqContent.subHeading}
						</Stack>
					</Stack>
					<Stack spacing={2}>
						{faqs.map((faq) => (
							<Faq
								key={faq.id}
								{...faq}
							/>
						))}
					</Stack>
				</Stack>
			</Container>
		</Box>
	);
}

export interface FaqProps {
	answer: string;
	question: string;
}

function Faq({ answer, question }: FaqProps) {
	const [isExpanded, setIsExpanded] = React.useState<boolean>(false);

	return (
		<Card
			elevation={2}
			sx={{ borderRadius: 4 }}>
			<CardContent
				sx={{
					p: { xs: 2.5, md: 3 },
					"&:last-child": {
						pb: { xs: 2.5, md: 3 },
					},
				}}>
				<Box
					onClick={() => {
						setIsExpanded((prevState) => !prevState);
					}}
					sx={{ cursor: "pointer" }}>
					<Stack
						direction="row"
						spacing={2}
						sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
						<Typography
							sx={{ fontFamily: headingFontFamily, pr: 2 }}
							variant="h6">
							{question}
						</Typography>
						<Box sx={{ color: "text.secondary", mt: 0.5 }}>
							{isExpanded ? <CaretDown /> : <CaretRight />}
						</Box>
					</Stack>
					<Collapse in={isExpanded}>
						<Typography
							color="text.secondary"
							sx={{ pt: 2 }}
							variant="body2">
							{answer}
						</Typography>
					</Collapse>
				</Box>
			</CardContent>
		</Card>
	);
}
